import { Box, Flex } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { ValuesOf } from '@tiptap/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  InputCard,
  TextInput,
  DateInput,
  SelectInput,
} from '../../../../common/DiffInputs';
import { getFormValues } from './dataMappings/getFormValues';
import { setFormValues } from './dataMappings/setFormValues';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { FullDateStr } from 'packages/common-frontend/components/InputsBETA/DateInput';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useScrollToAnchor } from 'packages/common-frontend/hooks/useScrollToAnchor';
import { useGetCreditFacilityKnowledgeBaseDocumentIdQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { FinleyThinkingSpinner } from 'packages/finleyAi/ui/FinleyAiThreadComponents';
import { initFinleyAiViewContext } from 'packages/finleyAi/ui/FinleyAiUtils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  fieldsToPromptAndZodSchema,
  FIELD_JSON_SCHEMAS,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/facility-mechanics/fieldToPrompt';
import { AutoSave } from '~/core/routes/settings/implementations/common/AutoSave';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import {
  useCreditAgreementStore,
  useGetCreditAgreementLazy,
} from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { DiffableFormData } from '~/core/routes/settings/implementations/common/types';
import { useFinleyAiThreads } from '~/packages/finleyAi/hooks/useFinleyAiThreads';

// Helper function to check if a value is a placeholder that should be ignored
const isPlaceholderValue = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  const placeholders = [
    '<UNKNOWN>',
    'UNKNOWN',
    'TBD',
    'N/A',
    'to be determined',
    'unknown',
  ];
  return placeholders.some((placeholder) =>
    value.toUpperCase().includes(placeholder.toUpperCase()),
  );
};

const AiExtractButton = ({
  label,
  isDisabled,
  fieldName,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiFlagEnabled,
}: {
  label: string;
  isDisabled?: boolean;
  fieldName: keyof CreditAgreement.DomainEntity.BaseFacilityMechanics;
  handleAiGenerateField?: (
    field: keyof CreditAgreement.DomainEntity.BaseFacilityMechanics,
  ) => void;
  isThreadLoading?: boolean;
  isFinleyAiFlagEnabled?: boolean;
}) => {
  if (!handleAiGenerateField || !isFinleyAiFlagEnabled) return <></>;
  if (isThreadLoading) return <FinleyThinkingSpinner />;
  return (
    <IconButton
      ariaLabel={label}
      label={label}
      icon={<Icon icon="wand-magic-sparkles" />}
      variant="outline"
      colorScheme="v2-aurora"
      onClick={() => handleAiGenerateField(fieldName)}
      isDisabled={isDisabled}
    />
  );
};

type FormComponentProps = {
  deleteProperty?: () => void;
  id: string;
  handleAiGenerateField?: (
    field: keyof CreditAgreement.DomainEntity.BaseFacilityMechanics,
  ) => void;
  isThreadLoading?: boolean;
  isFinleyAiDocumentAvailable?: boolean;
  isFinleyAiFlagEnabled?: boolean;
};

const AmortizationPeriodStart = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.amortizationPeriodStart"
    diffName="diffForm.amortizationPeriodStart"
    label="Amortization Period Start"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract amortization period start with Finley AI"
          fieldName="amortizationPeriodStart"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <DateInput<FormFields>
      name="wipForm.amortizationPeriodStart"
      diffName="diffForm.amortizationPeriodStart"
      label="Date"
      placeholder="Enter Date"
      helperText="Optional"
    />
  </InputCard>
);

const CallProtectionExpiration = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.callProtectionExpirationDate"
    diffName="diffForm.callProtectionExpirationDate"
    label="Call Protection Expiration"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          isThreadLoading={isThreadLoading}
          handleAiGenerateField={handleAiGenerateField}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
          label="Extract call protection expiration with Finley AI"
          fieldName="callProtectionExpirationDate"
        />
      </Flex>
    }
  >
    <DateInput<FormFields>
      name="wipForm.callProtectionExpirationDate"
      diffName="diffForm.callProtectionExpirationDate"
      label="Date"
      placeholder="Enter Date"
      helperText="Optional"
    />
  </InputCard>
);

const CallProtectionValue = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.callProtectionValue"
    diffName="diffForm.callProtectionValue"
    label="Call Protection Value"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          isDisabled={isFinleyAiDocumentAvailable}
          label="Extract call protection value with Finley AI"
          handleAiGenerateField={handleAiGenerateField}
          fieldName="callProtectionValue"
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
        />
      </Flex>
    }
  >
    <TextInput<FormFields>
      name="wipForm.callProtectionValue"
      diffName="diffForm.callProtectionValue"
      label="Value"
      helperText="Optional"
      mask="percentage"
    />
  </InputCard>
);

const ClosingDate = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.closingDate"
    diffName="diffForm.closingDate"
    label="Closing Date"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract closing date with Finley AI"
          fieldName="closingDate"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <DateInput<FormFields>
      name="wipForm.closingDate"
      diffName="diffForm.closingDate"
      label="Date"
      placeholder="Enter Date"
    />
  </InputCard>
);

const GoverningLaw = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.governingLaw"
    diffName="diffForm.governingLaw"
    label="Governing Law"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract governing law with Finley AI"
          fieldName="governingLaw"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <TextInput<FormFields>
      name="wipForm.governingLaw"
      diffName="diffForm.governingLaw"
      label="Location"
      placeholder="City, State"
      helperText="Optional"
    />
  </InputCard>
);

const MaturityDate = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.finalMaturityDate"
    diffName="diffForm.finalMaturityDate"
    label="Maturity Date"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract maturity date with Finley AI"
          fieldName="finalMaturityDate"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <DateInput<FormFields>
      name="wipForm.finalMaturityDate"
      diffName="diffForm.finalMaturityDate"
      label="Date"
      placeholder="Enter Date"
    />
    <TextInput<FormFields>
      name="wipForm.maturityDateDescription"
      diffName="diffForm.maturityDateDescription"
      label="Description"
      helperText="Optional"
    />
  </InputCard>
);

const MinimumUtilization = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.minimumUtilization"
    diffName="diffForm.minimumUtilization"
    label="Minimum Utilization"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract minimum utilization with Finley AI"
          fieldName="minimumUtilization"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <TextInput<FormFields>
      name="wipForm.minimumUtilization"
      diffName="diffForm.minimumUtilization"
      label="Value"
      helperText="Optional"
      mask="percentage"
    />
  </InputCard>
);

const TermLoan = ({
  deleteProperty,
  id,
  handleAiGenerateField: _handleAiGenerateField,
  isThreadLoading: _isThreadLoading,
  isFinleyAiDocumentAvailable: _isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled: _isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard
    name="wipForm.termLoan"
    diffName="diffForm.termLoan"
    label="Term Loan"
    deleteProperty={deleteProperty}
    id={id}
  >
    <SelectInput<FormFields>
      name="wipForm.termLoan"
      diffName="diffForm.termLoan"
      label="Type"
      placeholder="Select type"
      options={[
        {
          label: 'Yes',
          value: 'true',
        },
        {
          label: 'No',
          value: 'false',
        },
      ]}
    />
  </InputCard>
);

const YieldMaintenance = ({
  deleteProperty,
  id,
  handleAiGenerateField,
  isThreadLoading,
  isFinleyAiDocumentAvailable,
  isFinleyAiFlagEnabled,
}: FormComponentProps) => (
  <InputCard<FormFields>
    name="wipForm.yieldMaintenance"
    diffName="diffForm.yieldMaintenance"
    label="Yield Maintenance"
    deleteProperty={deleteProperty}
    id={id}
    headerChildren={
      <Flex gap="0.5rem" alignItems="center">
        <AiExtractButton
          label="Extract yield maintenance with Finley AI"
          fieldName="yieldMaintenance"
          handleAiGenerateField={handleAiGenerateField}
          isThreadLoading={isThreadLoading}
          isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
          isDisabled={isFinleyAiDocumentAvailable}
        />
      </Flex>
    }
  >
    <DateInput<FormFields>
      name="wipForm.yieldMaintenance"
      diffName="diffForm.yieldMaintenance"
      label="Date"
      placeholder="Enter Date"
      helperText="Optional"
    />
  </InputCard>
);

export const PROPERTY_ITEMS = [
  {
    name: 'closingDate',
    label: 'Closing Date',
    component: ClosingDate,
    required: true,
  },
  {
    name: 'finalMaturityDate',
    label: 'Maturity Date',
    component: MaturityDate,
    required: true,
  },
  {
    name: 'termLoan',
    label: 'Term Loan',
    component: TermLoan,
    required: true,
  },
  {
    name: 'amortizationPeriodStart',
    label: 'Amortization Period Start',
    component: AmortizationPeriodStart,
    required: false,
  },
  {
    name: 'callProtectionExpirationDate',
    label: 'Call Protection Expiration',
    component: CallProtectionExpiration,
    required: false,
  },
  {
    name: 'callProtectionValue',
    label: 'Call Protection Value',
    component: CallProtectionValue,
    required: false,
  },
  {
    name: 'governingLaw',
    label: 'Governing Law',
    component: GoverningLaw,
    required: false,
  },
  {
    name: 'minimumUtilization',
    label: 'Minimum Utilization',
    component: MinimumUtilization,
    required: false,
  },
  {
    name: 'yieldMaintenance',
    label: 'Yield Maintenance',
    component: YieldMaintenance,
    required: false,
  },
] as const;

type PropertyItem = typeof PROPERTY_ITEMS[number];

export type BaseFormFields = {
  amortizationPeriodStart?: FullDateStr | null;
  callProtectionExpirationDate?: FullDateStr | null;
  callProtectionValue?: number | null;
  closingDate: FullDateStr | null;
  governingLaw?: string;
  finalMaturityDate: FullDateStr | null;
  maturityDateDescription?: string;
  minimumUtilization?: number | null;
  yieldMaintenance?: FullDateStr | null;
  termLoan: 'true' | 'false';
};

export type FormFields = DiffableFormData<BaseFormFields>;

export const FacilityMechanics = () => {
  useScrollToAnchor();
  const getCreditAgreementOrThrow = useGetCreditAgreementLazy();
  const { user } = useUserAuthOrThrow();

  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  const defaultValues = getFormValues(getCreditAgreementOrThrow());
  const form = useForm<FormFields>({
    defaultValues: { wipForm: defaultValues },
  });
  const { setValue } = form;

  const [properties, setProperties] = useState<PropertyItem[]>(
    PROPERTY_ITEMS.filter(({ name }) => defaultValues[name] !== undefined),
  );

  const previousCreditAgreement = useCreditAgreementStore(
    (store) => store.previousCreditAgreement,
  );

  const creditFacilityId = useCreditAgreementStore(
    (store) => store.creditAgreement?.creditFacilityId,
  );
  const {
    data: knowledgeDocumentIdQuery,
    loading: isKnowledgeBaseDocumentIdLoading,
  } = useGetCreditFacilityKnowledgeBaseDocumentIdQuery({
    variables: {
      creditFacilityId: creditFacilityId ?? '',
    },
  });
  const knowledgeBaseDocumentId =
    knowledgeDocumentIdQuery?.creditFacility.knowledgeBaseDocumentId ?? '';

  // Individual field prompts for multi-threading
  const [fieldPrompts, setFieldPrompts] = useState<Record<string, string>>({});

  // Memoized view contexts for each field
  const fieldViewContexts = useMemo(() => {
    const baseContext = {
      targetOrganizationId: user.organizationId,
      type: 'documentAma' as const,
      knowledgeBaseDocumentIds: knowledgeBaseDocumentId
        ? [knowledgeBaseDocumentId]
        : [],
      keywords: [], // todo: @implementation-agent
    };

    return {
      amortizationPeriodStart: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.amortizationPeriodStart,
      }),
      callProtectionExpirationDate: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.callProtectionExpirationDate,
      }),
      callProtectionValue: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.callProtectionValue,
      }),
      closingDate: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.closingDate,
      }),
      governingLaw: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.governingLaw,
      }),
      finalMaturityDate: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.finalMaturityDate,
      }),
      minimumUtilization: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.minimumUtilization,
      }),
      structure: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.structure,
      }),
      yieldMaintenance: initFinleyAiViewContext({
        ...baseContext,
        tool: FIELD_JSON_SCHEMAS.yieldMaintenance,
      }),
    };
  }, [user.organizationId, knowledgeBaseDocumentId]);

  // Individual field threads - called at top level to comply with Rules of Hooks
  const amortizationPeriodStartThread = useFinleyAiThreads({
    prompt: fieldPrompts.amortizationPeriodStart || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        amortizationPeriodStart:
          typeof p === 'function' ? p(prev.amortizationPeriodStart || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.amortizationPeriodStart,
  });

  const callProtectionExpirationDateThread = useFinleyAiThreads({
    prompt: fieldPrompts.callProtectionExpirationDate || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        callProtectionExpirationDate:
          typeof p === 'function'
            ? p(prev.callProtectionExpirationDate || '')
            : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.callProtectionExpirationDate,
  });

  const callProtectionValueThread = useFinleyAiThreads({
    prompt: fieldPrompts.callProtectionValue || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        callProtectionValue:
          typeof p === 'function' ? p(prev.callProtectionValue || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.callProtectionValue,
  });

  const closingDateThread = useFinleyAiThreads({
    prompt: fieldPrompts.closingDate || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        closingDate: typeof p === 'function' ? p(prev.closingDate || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.closingDate,
  });

  const governingLawThread = useFinleyAiThreads({
    prompt: fieldPrompts.governingLaw || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        governingLaw: typeof p === 'function' ? p(prev.governingLaw || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.governingLaw,
  });

  const finalMaturityDateThread = useFinleyAiThreads({
    prompt: fieldPrompts.finalMaturityDate || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        finalMaturityDate:
          typeof p === 'function' ? p(prev.finalMaturityDate || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.finalMaturityDate,
  });

  const minimumUtilizationThread = useFinleyAiThreads({
    prompt: fieldPrompts.minimumUtilization || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        minimumUtilization:
          typeof p === 'function' ? p(prev.minimumUtilization || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.minimumUtilization,
  });

  const structureThread = useFinleyAiThreads({
    prompt: fieldPrompts.structure || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        structure: typeof p === 'function' ? p(prev.structure || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.structure,
  });

  const yieldMaintenanceThread = useFinleyAiThreads({
    prompt: fieldPrompts.yieldMaintenance || '',
    setPrompt: (p) =>
      setFieldPrompts((prev) => ({
        ...prev,
        yieldMaintenance:
          typeof p === 'function' ? p(prev.yieldMaintenance || '') : p,
      })),
    tempPrompt: '',
    setTempPrompt: () => {},
    initialViewContext: fieldViewContexts.yieldMaintenance,
  });

  // Memoized fieldThreads object to prevent unnecessary re-renders
  const fieldThreads = useMemo(
    () => ({
      amortizationPeriodStart: amortizationPeriodStartThread,
      callProtectionExpirationDate: callProtectionExpirationDateThread,
      callProtectionValue: callProtectionValueThread,
      closingDate: closingDateThread,
      governingLaw: governingLawThread,
      finalMaturityDate: finalMaturityDateThread,
      minimumUtilization: minimumUtilizationThread,
      structure: structureThread,
      yieldMaintenance: yieldMaintenanceThread,
    }),
    [
      amortizationPeriodStartThread,
      callProtectionExpirationDateThread,
      callProtectionValueThread,
      closingDateThread,
      governingLawThread,
      finalMaturityDateThread,
      minimumUtilizationThread,
      structureThread,
      yieldMaintenanceThread,
    ],
  );

  useEffect(() => {
    if (knowledgeBaseDocumentId) {
      // Update view context for all field threads individually with their specific schemas
      fieldThreads.amortizationPeriodStart.setViewContext(
        fieldViewContexts.amortizationPeriodStart,
      );
      fieldThreads.callProtectionExpirationDate.setViewContext(
        fieldViewContexts.callProtectionExpirationDate,
      );
      fieldThreads.callProtectionValue.setViewContext(
        fieldViewContexts.callProtectionValue,
      );
      fieldThreads.closingDate.setViewContext(fieldViewContexts.closingDate);
      fieldThreads.governingLaw.setViewContext(fieldViewContexts.governingLaw);
      fieldThreads.finalMaturityDate.setViewContext(
        fieldViewContexts.finalMaturityDate,
      );
      fieldThreads.minimumUtilization.setViewContext(
        fieldViewContexts.minimumUtilization,
      );
      fieldThreads.structure.setViewContext(fieldViewContexts.structure);
      fieldThreads.yieldMaintenance.setViewContext(
        fieldViewContexts.yieldMaintenance,
      );
    }
  }, [
    fieldThreads.amortizationPeriodStart,
    fieldThreads.callProtectionExpirationDate,
    fieldThreads.callProtectionValue,
    fieldThreads.closingDate,
    fieldThreads.finalMaturityDate,
    fieldThreads.governingLaw,
    fieldThreads.minimumUtilization,
    fieldThreads.structure,
    fieldThreads.yieldMaintenance,
    fieldViewContexts,
    knowledgeBaseDocumentId,
  ]);

  const handleAiGenerateField = useCallback(
    async (field: keyof CreditAgreement.DomainEntity.BaseFacilityMechanics) => {
      // Use individual field thread (each has its own context with correct schema)
      const fieldThread = fieldThreads[field as keyof typeof fieldThreads];
      if (fieldThread) {
        fieldThread.onSendPrompt(fieldsToPromptAndZodSchema[field].prompt);
      }
    },
    [fieldThreads],
  );

  // Process all fields simultaneously - for section level processing
  const processAllFields = useCallback(async () => {
    const fieldsToProcess = Object.keys(fieldsToPromptAndZodSchema) as Array<
      keyof typeof fieldsToPromptAndZodSchema
    >;

    // Fire off all field threads simultaneously
    fieldsToProcess.forEach((field) => {
      const fieldThread = fieldThreads[field as keyof typeof fieldThreads];
      if (fieldThread) {
        fieldThread.onSendPrompt(fieldsToPromptAndZodSchema[field].prompt);
      }
    });
  }, [fieldThreads]);

  // Process individual field thread responses
  useEffect(() => {
    const processFieldResponse = (
      fieldName: keyof CreditAgreement.DomainEntity.BaseFacilityMechanics,
      thread: ReturnType<typeof useFinleyAiThreads>,
    ) => {
      if (!thread.selectedThread || thread.isThreadLoading) return;

      const promptResult = thread.selectedThread?.promptResponses?.at(-1)
        ?.promptResult;

      if (!promptResult) return;

      const fieldJson =
        promptResult.type === 'documentAma' &&
        promptResult.subType === 'question'
          ? (promptResult.toolInput as ValuesOf<CreditAgreement.DomainEntity.BaseFacilityMechanics>)
          : 'toolInput not available for this prompt result type';

      if (fieldJson && typeof fieldJson !== 'string') {
        switch (fieldName) {
          case 'closingDate':
            if (
              fieldJson.value &&
              !isPlaceholderValue(fieldJson.value) &&
              typeof fieldJson.value === 'string'
            ) {
              const convertedDate = commonDate.validateFullDateStr(
                fieldJson.value,
              );
              setValue('wipForm.closingDate', convertedDate);
            }
            break;
          case 'finalMaturityDate':
            if (
              fieldJson.value &&
              !isPlaceholderValue(fieldJson.value) &&
              typeof fieldJson.value === 'string'
            ) {
              setValue(
                'wipForm.finalMaturityDate',
                commonDate.validateFullDateStr(fieldJson.value),
              );
              setValue(
                'wipForm.maturityDateDescription',
                fieldJson.description,
              );
            }
            break;
          case 'yieldMaintenance':
            if (
              fieldJson.value &&
              !isPlaceholderValue(fieldJson.value) &&
              typeof fieldJson.value === 'string'
            ) {
              setValue(
                'wipForm.yieldMaintenance',
                commonDate.validateFullDateStr(fieldJson.value),
              );
            }
            break;
          case 'governingLaw':
            if (
              typeof fieldJson.value === 'string' &&
              !isPlaceholderValue(fieldJson.value)
            ) {
              setValue('wipForm.governingLaw', fieldJson.value);
            }
            break;
          case 'minimumUtilization':
            if (fieldJson.value && !isPlaceholderValue(fieldJson.value)) {
              setValue('wipForm.minimumUtilization', Number(fieldJson.value));
            }
            break;
          case 'amortizationPeriodStart':
            if (fieldJson.value && !isPlaceholderValue(fieldJson.value)) {
              setValue(
                'wipForm.amortizationPeriodStart',
                commonDate.validateFullDateStr(fieldJson.value),
              );
            }
            break;
          case 'callProtectionExpirationDate':
            if (fieldJson.value && !isPlaceholderValue(fieldJson.value)) {
              setValue(
                'wipForm.callProtectionExpirationDate',
                commonDate.validateFullDateStr(fieldJson.value),
              );
            }
            break;
          case 'callProtectionValue':
            if (fieldJson.value && !isPlaceholderValue(fieldJson.value)) {
              setValue('wipForm.callProtectionValue', Number(fieldJson.value));
            }
            break;
          default:
            // No action needed for unhandled field names
            break;
        }
      }
    };

    // Process each field thread individually
    processFieldResponse(
      'amortizationPeriodStart',
      fieldThreads.amortizationPeriodStart,
    );
    processFieldResponse(
      'callProtectionExpirationDate',
      fieldThreads.callProtectionExpirationDate,
    );
    processFieldResponse(
      'callProtectionValue',
      fieldThreads.callProtectionValue,
    );
    processFieldResponse('closingDate', fieldThreads.closingDate);
    processFieldResponse('governingLaw', fieldThreads.governingLaw);
    processFieldResponse('finalMaturityDate', fieldThreads.finalMaturityDate);
    processFieldResponse('minimumUtilization', fieldThreads.minimumUtilization);
    processFieldResponse('structure', fieldThreads.structure);
    processFieldResponse('yieldMaintenance', fieldThreads.yieldMaintenance);
  }, [
    setValue,
    fieldThreads.amortizationPeriodStart,
    fieldThreads.callProtectionExpirationDate,
    fieldThreads.callProtectionValue,
    fieldThreads.closingDate,
    fieldThreads.governingLaw,
    fieldThreads.finalMaturityDate,
    fieldThreads.minimumUtilization,
    fieldThreads.structure,
    fieldThreads.yieldMaintenance,
  ]);

  useEffect(() => {
    if (previousCreditAgreement) {
      const previousFormValues = getFormValues(previousCreditAgreement);
      form.setValue('diffForm', previousFormValues);
    }
  }, [form, previousCreditAgreement]);

  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const appendProperty = (property: PropertyItem) =>
    setProperties((prevProperties) => [...prevProperties, property]);

  const deleteProperty = (property: PropertyItem | null) => {
    if (property !== null) {
      setProperties((prevProperties) =>
        prevProperties.filter(
          (prevProperty) => prevProperty.name !== property.name,
        ),
      );
      form.resetField(`wipForm.${property.name}`);
    }
  };

  const propertySet = new Set(properties.map((property) => property.name));

  const { wipForm, diffForm } = form.getValues();

  const diffProperties = PROPERTY_ITEMS.filter(
    ({ name }) => wipForm[name] !== undefined || diffForm?.[name] !== undefined,
  );

  const propertiesToRender = isEditing ? properties : diffProperties;

  const isFinleyAiDocumentAvailable =
    !isKnowledgeBaseDocumentIdLoading && !knowledgeBaseDocumentId;

  const flags = useFeatureFlags();
  const isFinleyAiFlagEnabled =
    flags.productsImplementationPortalCreditAgreementAutomationPermissions ===
    'allow';

  return (
    <Flex direction="column">
      <Flex justify="space-between" mb="1rem">
        <Typography variant="page-title" trim>
          Facility Mechanics
        </Typography>
        <Flex gap="1rem" alignItems="center">
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="v2-aurora"
              rightIcon={<Icon icon="chevron-down" />}
              variant="outline"
            >
              Add Property
            </MenuButton>
            <MenuList>
              {PROPERTY_ITEMS.map((property) => (
                <MenuItem
                  key={property.name}
                  isDisabled={propertySet.has(property.name)}
                  onClick={() => appendProperty(property)}
                >
                  {property.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          {isFinleyAiFlagEnabled && (
            <Button
              colorScheme="v2-aurora"
              variant="outline"
              onClick={processAllFields}
              isDisabled={isFinleyAiDocumentAvailable}
              leftIcon={<Icon icon="wand-magic-sparkles" />}
            >
              All Fields
            </Button>
          )}
        </Flex>
      </Flex>

      {isFinleyAiFlagEnabled && isFinleyAiDocumentAvailable && (
        <Alert status="info" my="1rem">
          <AlertIcon />
          <AlertDescription>
            Please link a document to use Finley AI.
          </AlertDescription>
        </Alert>
      )}

      {propertiesToRender.length === 0 && (
        <Typography variant="base-muted">
          No properties have been added yet. Use the &quot;Add Property&quot;
          button above to select and configure the desired fields.
        </Typography>
      )}

      {propertiesToRender.length > 0 && (
        <>
          <Typography variant="section-subtitle">Properties</Typography>
          <Box>
            <FormProvider {...form}>
              <AutoSave<FormFields> onSave={setFormValues} />
              <form>
                {propertiesToRender.map((property) => {
                  const Component = property.component;
                  const onDeleteProperty = () =>
                    showConfirmDeleteModal(property.label, () =>
                      deleteProperty(property),
                    );
                  return (
                    <Component
                      id={property.name}
                      key={property.name}
                      deleteProperty={
                        !property.required ? onDeleteProperty : undefined
                      }
                      isThreadLoading={
                        fieldThreads[property.name as keyof typeof fieldThreads]
                          ?.isThreadLoading || false
                      }
                      handleAiGenerateField={handleAiGenerateField}
                      isFinleyAiDocumentAvailable={isFinleyAiDocumentAvailable}
                      isFinleyAiFlagEnabled={isFinleyAiFlagEnabled}
                    />
                  );
                })}
              </form>
            </FormProvider>
          </Box>
        </>
      )}
    </Flex>
  );
};
